import { useState, useEffect, useCallback } from 'react';
import { 
  Grid3X3, Monitor, Calendar, Building, Truck, DollarSign, 
  Check, X, AlertCircle, Clock, Search, Filter, Plus, List,
  FileText, CreditCard, Eye, RefreshCw
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { storesAPI, revenueAPI, manufacturersAPI, vendorsAPI } from '@/services/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to format date in Indian format (DD/MM/YYYY)
const formatIndianDate = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

// Cell type colors
const CELL_TYPE_COLORS = {
  pro_premium: { bg: 'bg-purple-500', text: 'text-white', label: 'Pro Premium', border: 'border-purple-600' },
  premium: { bg: 'bg-blue-500', text: 'text-white', label: 'Premium', border: 'border-blue-600' },
  standard: { bg: 'bg-gray-400', text: 'text-white', label: 'Standard', border: 'border-gray-500' },
  free: { bg: 'bg-green-500', text: 'text-white', label: 'Free', border: 'border-green-600' }
};

// Display type colors
const DISPLAY_TYPE_COLORS = {
  premium: { bg: 'bg-amber-500', text: 'text-white', label: 'Premium' },
  standard: { bg: 'bg-gray-400', text: 'text-white', label: 'Standard' }
};

const DURATION_OPTIONS = [
  { value: 1, label: '1 Month' },
  { value: 3, label: '3 Months' },
  { value: 6, label: '6 Months' },
  { value: 12, label: '12 Months' }
];

const ShelfBookingPage = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('book'); // 'book' or 'list'
  const [stores, setStores] = useState([]);
  const [selectedStore, setSelectedStore] = useState(null);
  const [rackSettings, setRackSettings] = useState({ racks: [] });
  const [bookedCells, setBookedCells] = useState({});
  const [bookedDisplays, setBookedDisplays] = useState({});
  const [manufacturers, setManufacturers] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [bookingSettings, setBookingSettings] = useState(null);
  
  // Bookings list state
  const [bookingsList, setBookingsList] = useState([]);
  const [loadingBookings, setLoadingBookings] = useState(false);
  const [bookingsFilter, setBookingsFilter] = useState('all'); // 'all', 'active', 'expired'
  
  // Booking dialog state
  const [bookingDialogOpen, setBookingDialogOpen] = useState(false);
  const [bookingType, setBookingType] = useState('cell'); // 'cell' or 'display'
  const [selectedCell, setSelectedCell] = useState(null);
  const [selectedDisplay, setSelectedDisplay] = useState(null);
  const [bookingForm, setBookingForm] = useState({
    party_type: 'manufacturer',
    party_id: '',
    duration_months: 6,
    start_date: new Date().toISOString().split('T')[0],
    custom_discount: null,
    notes: ''
  });
  const [pricing, setPricing] = useState(null);
  const [calculatingPrice, setCalculatingPrice] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [savingBooking, setSavingBooking] = useState(false);
  
  // View booking detail
  const [viewBookingDialog, setViewBookingDialog] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);

  // Fetch stores
  const fetchStores = useCallback(async () => {
    try {
      const response = await storesAPI.list();
      setStores(response.data || []);
      if (response.data?.length > 0 && !selectedStore) {
        setSelectedStore(response.data[0]);
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to fetch stores', variant: 'destructive' });
    }
  }, [toast, selectedStore]);

  // Fetch rack settings for selected store
  const fetchRackSettings = useCallback(async () => {
    if (!selectedStore) return;
    try {
      const response = await revenueAPI.getStoreRackSettings(selectedStore.id);
      setRackSettings(response.data || { racks: [] });
    } catch (error) {
      console.error('Failed to fetch rack settings:', error);
    }
  }, [selectedStore]);

  // Fetch booked cells and displays
  const fetchBookedItems = useCallback(async () => {
    if (!selectedStore) return;
    const token = localStorage.getItem('access_token');
    try {
      const [cellsRes, displaysRes] = await Promise.all([
        fetch(`${API_URL}/api/bookings/stores/${selectedStore.id}/booked-cells`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/bookings/stores/${selectedStore.id}/booked-displays`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);
      
      const cellsData = await cellsRes.json();
      const displaysData = await displaysRes.json();
      
      setBookedCells(cellsData.booked_cells || {});
      setBookedDisplays(cellsData.booked_displays || {});
    } catch (error) {
      console.error('Failed to fetch booked items:', error);
    }
  }, [selectedStore]);

  // Fetch parties
  const fetchParties = useCallback(async () => {
    try {
      const [mfgrRes, vendorRes] = await Promise.all([
        manufacturersAPI.dropdown(),
        vendorsAPI.dropdown()
      ]);
      setManufacturers(mfgrRes.data || []);
      setVendors(vendorRes.data || []);
    } catch (error) {
      console.error('Failed to fetch parties:', error);
    }
  }, []);

  // Fetch booking settings
  const fetchBookingSettings = useCallback(async () => {
    const token = localStorage.getItem('access_token');
    try {
      const response = await fetch(`${API_URL}/api/bookings/settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setBookingSettings(data);
    } catch (error) {
      console.error('Failed to fetch booking settings:', error);
    }
  }, []);

  // Fetch bookings list
  const fetchBookingsList = useCallback(async () => {
    if (!selectedStore) return;
    setLoadingBookings(true);
    const token = localStorage.getItem('access_token');
    try {
      let url = `${API_URL}/api/bookings/shelf?store_id=${selectedStore.id}&page_size=100`;
      if (bookingsFilter === 'active') {
        url += `&status=active`;
      } else if (bookingsFilter === 'expired') {
        url += `&status=expired`;
      }
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      
      // Enrich with party names
      const enrichedBookings = (data.items || []).map(booking => {
        let partyName = '-';
        if (booking.party_type === 'manufacturer') {
          const mfgr = manufacturers.find(m => m.id === booking.party_id);
          partyName = mfgr?.company_name || booking.party_id;
        } else {
          const vendor = vendors.find(v => v.id === booking.party_id);
          partyName = vendor?.name || booking.party_id;
        }
        return { ...booking, party_name: partyName };
      });
      
      setBookingsList(enrichedBookings);
    } catch (error) {
      console.error('Failed to fetch bookings list:', error);
      setBookingsList([]);
    } finally {
      setLoadingBookings(false);
    }
  }, [selectedStore, bookingsFilter, manufacturers, vendors]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchStores(), fetchParties(), fetchBookingSettings()]);
      setLoading(false);
    };
    loadData();
  }, [fetchStores, fetchParties, fetchBookingSettings]);

  useEffect(() => {
    if (selectedStore) {
      fetchRackSettings();
      fetchBookedItems();
      if (activeTab === 'list') {
        fetchBookingsList();
      }
    }
  }, [selectedStore, fetchRackSettings, fetchBookedItems, activeTab, fetchBookingsList]);

  // Calculate pricing when booking form changes
  const calculatePricing = useCallback(async () => {
    if (!selectedStore || (!selectedCell && !selectedDisplay)) return;
    
    setCalculatingPrice(true);
    const token = localStorage.getItem('access_token');
    
    try {
      let url;
      if (bookingType === 'cell' && selectedCell) {
        url = `${API_URL}/api/bookings/calculate-price/cell?store_id=${selectedStore.id}&cell_id=${selectedCell.cell_id}&duration_months=${bookingForm.duration_months}`;
        if (bookingForm.custom_discount !== null) {
          url += `&custom_discount=${bookingForm.custom_discount}`;
        }
      } else if (bookingType === 'display' && selectedDisplay) {
        url = `${API_URL}/api/bookings/calculate-price/display?store_id=${selectedStore.id}&rack_id=${selectedDisplay.rack_id}&column=${selectedDisplay.column}&duration_months=${bookingForm.duration_months}`;
        if (bookingForm.custom_discount !== null) {
          url += `&custom_discount=${bookingForm.custom_discount}`;
        }
      }
      
      if (url) {
        const response = await fetch(url, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await response.json();
        setPricing(data);
      }
    } catch (error) {
      console.error('Failed to calculate pricing:', error);
    } finally {
      setCalculatingPrice(false);
    }
  }, [selectedStore, selectedCell, selectedDisplay, bookingType, bookingForm.duration_months, bookingForm.custom_discount]);

  useEffect(() => {
    if (bookingDialogOpen) {
      calculatePricing();
    }
  }, [bookingDialogOpen, calculatePricing]);

  // Handle cell click
  const handleCellClick = (rack, col, row) => {
    const cellId = `${rack.rack_id}-${col}-${row}`;
    
    // Check if booked
    if (bookedCells[cellId]) {
      toast({
        title: 'Cell Booked',
        description: `Booked by ${bookedCells[cellId].party_name} until ${bookedCells[cellId].end_date}`,
        variant: 'default'
      });
      return;
    }
    
    // Find cell type
    const cell = rack.cells?.find(c => c.cell_id === cellId);
    const cellType = cell?.type || 'standard';
    
    setSelectedCell({ cell_id: cellId, rack_id: rack.rack_id, column: col, row, type: cellType });
    setSelectedDisplay(null);
    setBookingType('cell');
    setPricing(null);
    setBookingDialogOpen(true);
  };

  // Handle display click
  const handleDisplayClick = (rack, col) => {
    const displayKey = `${rack.rack_id}-${col}`;
    
    // Check if booked
    if (bookedDisplays[displayKey]) {
      toast({
        title: 'Display Booked',
        description: `Booked by ${bookedDisplays[displayKey].party_name} until ${bookedDisplays[displayKey].end_date}`,
        variant: 'default'
      });
      return;
    }
    
    // Find display config
    const display = rack.column_displays?.find(d => d.column === col);
    if (!display?.has_display) {
      toast({ title: 'No Display', description: 'This column does not have a display unit', variant: 'default' });
      return;
    }
    
    setSelectedDisplay({ rack_id: rack.rack_id, column: col, display_type: display.display_type || 'standard' });
    setSelectedCell(null);
    setBookingType('display');
    setPricing(null);
    setBookingDialogOpen(true);
  };

  // Calculate end date (returns ISO format for API)
  const calculateEndDate = (startDate, months) => {
    const start = new Date(startDate);
    start.setMonth(start.getMonth() + months);
    start.setDate(start.getDate() - 1);
    return start.toISOString().split('T')[0];
  };

  // Calculate end date in Indian format for display
  const calculateEndDateIndian = (startDate, months) => {
    const endDateISO = calculateEndDate(startDate, months);
    return formatIndianDate(endDateISO);
  };

  // View booking details
  const handleViewBooking = (booking) => {
    setSelectedBooking(booking);
    setViewBookingDialog(true);
  };

  // Get days until expiry
  const getDaysUntilExpiry = (endDate) => {
    const end = new Date(endDate);
    const today = new Date();
    const diffTime = end - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Get expiry status badge
  const getExpiryBadge = (endDate) => {
    const days = getDaysUntilExpiry(endDate);
    if (days < 0) {
      return <Badge className="bg-red-100 text-red-700">Expired</Badge>;
    } else if (days <= 7) {
      return <Badge className="bg-orange-100 text-orange-700">Expires in {days}d</Badge>;
    } else if (days <= 30) {
      return <Badge className="bg-yellow-100 text-yellow-700">Expires in {days}d</Badge>;
    }
    return <Badge className="bg-green-100 text-green-700">Active</Badge>;
  };

  // Handle booking confirmation
  const handleConfirmBooking = async () => {
    if (!bookingForm.party_id) {
      toast({ title: 'Error', description: 'Please select a party', variant: 'destructive' });
      return;
    }
    setConfirmDialogOpen(true);
  };

  // Create booking
  const handleCreateBooking = async () => {
    setSavingBooking(true);
    const token = localStorage.getItem('access_token');
    
    try {
      const endpoint = bookingType === 'cell' ? '/api/bookings/shelf' : '/api/bookings/display';
      
      const payload = {
        store_id: selectedStore.id,
        party_type: bookingForm.party_type,
        party_id: bookingForm.party_id,
        duration_months: bookingForm.duration_months,
        start_date: bookingForm.start_date,
        notes: bookingForm.notes || null
      };
      
      if (bookingType === 'cell') {
        payload.rack_id = selectedCell.rack_id;
        payload.cell_id = selectedCell.cell_id;
      } else {
        payload.rack_id = selectedDisplay.rack_id;
        payload.column = selectedDisplay.column;
      }
      
      if (bookingForm.custom_discount !== null) {
        payload.discount_percent = bookingForm.custom_discount;
      }
      
      const response = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast({ 
          title: 'Success', 
          description: `Booking ${data.booking_number} created. Invoice generated.` 
        });
        setConfirmDialogOpen(false);
        setBookingDialogOpen(false);
        fetchBookedItems();
        // Reset form
        setBookingForm({
          party_type: 'manufacturer',
          party_id: '',
          duration_months: 6,
          start_date: new Date().toISOString().split('T')[0],
          custom_discount: null,
          notes: ''
        });
      } else {
        toast({ title: 'Error', description: data.detail || 'Failed to create booking', variant: 'destructive' });
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to create booking', variant: 'destructive' });
    } finally {
      setSavingBooking(false);
    }
  };

  // Get cell from rack
  const getCellFromRack = (rack, col, row) => {
    const cellId = `${rack.rack_id}-${col}-${row}`;
    return rack.cells?.find(c => c.cell_id === cellId) || { cell_id: cellId, type: 'standard' };
  };

  // Get display from rack
  const getDisplayFromRack = (rack, col) => {
    return rack.column_displays?.find(d => d.column === col) || { column: col, has_display: false };
  };

  // Check if cell is booked
  const isCellBooked = (cellId) => !!bookedCells[cellId];

  // Check if display is booked
  const isDisplayBooked = (rackId, column) => !!bookedDisplays[`${rackId}-${column}`];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="shelf-booking-page">
      <Toaster />
      
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Shelf & Display Booking</h1>
          <p className="text-gray-500 text-sm">Book shelf cells and display ad spaces for vendors/manufacturers</p>
        </div>
      </div>

      {/* Store Selector */}
      <Card className="bg-white border-gray-200">
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <Label className="text-gray-700 font-medium whitespace-nowrap">Select Store:</Label>
            <Select 
              value={selectedStore?.id || ''} 
              onValueChange={(v) => setSelectedStore(stores.find(s => s.id === v))}
            >
              <SelectTrigger className="max-w-md bg-gray-50 border-gray-300">
                <SelectValue placeholder="Select a store" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                {stores.map(store => (
                  <SelectItem key={store.id} value={store.id}>
                    {store.name} ({store.code})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Tabs for Book / List View */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-gray-100">
          <TabsTrigger value="book" className="data-[state=active]:bg-white">
            <Grid3X3 className="w-4 h-4 mr-2" /> Book Shelf/Display
          </TabsTrigger>
          <TabsTrigger value="list" className="data-[state=active]:bg-white">
            <List className="w-4 h-4 mr-2" /> Bookings List
          </TabsTrigger>
        </TabsList>

        {/* Book Tab Content */}
        <TabsContent value="book" className="mt-4 space-y-4">
          {/* Legend */}
          <Card className="bg-white border-gray-200">
            <CardContent className="p-4">
              <div className="flex flex-wrap items-center gap-6">
                <span className="text-sm text-gray-600 font-medium">Cell Types:</span>
                {Object.entries(CELL_TYPE_COLORS).map(([key, value]) => (
                  <div key={key} className="flex items-center gap-2">
                    <div className={`w-4 h-4 rounded ${value.bg}`}></div>
                    <span className="text-sm text-gray-600">{value.label}</span>
                  </div>
                ))}
                <div className="h-4 border-l border-gray-300 mx-2"></div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded bg-red-500 opacity-50"></div>
                  <span className="text-sm text-gray-600">Booked</span>
                </div>
                <div className="h-4 border-l border-gray-300 mx-2"></div>
                <div className="flex items-center gap-2">
                  <Monitor className="w-4 h-4 text-amber-500" />
                  <span className="text-sm text-gray-600">Display Ad</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Rack Grid */}
          {rackSettings.racks.length === 0 ? (
            <Card className="bg-white border-gray-200">
              <CardContent className="py-12 text-center">
                <Grid3X3 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-lg">No racks configured for this store</p>
            <p className="text-gray-400 text-sm">Configure rack layout in Store Settings first</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          {rackSettings.racks.map((rack) => (
            <Card key={rack.rack_id} className="bg-white border-gray-200" data-testid={`rack-${rack.rack_id}`}>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg text-gray-800 flex items-center gap-2">
                  <Grid3X3 className="w-5 h-5 text-indigo-500" />
                  {rack.rack_id}
                  {rack.rack_name && <span className="text-gray-500 font-normal text-sm">- {rack.rack_name}</span>}
                </CardTitle>
                <CardDescription>Click on available cells or displays to book</CardDescription>
              </CardHeader>
              <CardContent>
                {/* Display Ad Row */}
                <div className="mb-3">
                  <p className="text-xs text-gray-500 mb-1 ml-8">Display Ads (click to book)</p>
                  <div className="flex gap-1 ml-8">
                    {(rack.columns || []).map((col) => {
                      const display = getDisplayFromRack(rack, col);
                      const isBooked = isDisplayBooked(rack.rack_id, col);
                      
                      if (!display.has_display) {
                        return <div key={col} className="flex-1 h-8"></div>;
                      }
                      
                      return (
                        <div key={col} className="flex-1 flex justify-center">
                          <button
                            onClick={() => handleDisplayClick(rack, col)}
                            className={`p-1.5 rounded transition-all ${
                              isBooked 
                                ? 'bg-red-100 text-red-400 cursor-not-allowed' 
                                : display.display_type === 'premium'
                                  ? 'bg-amber-100 text-amber-600 hover:bg-amber-200'
                                  : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                            }`}
                            title={isBooked ? 'Booked' : `${display.display_type || 'Standard'} Display - Click to book`}
                          >
                            <Monitor className="w-5 h-5" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Column Headers */}
                <div className="flex gap-1 mb-1 ml-8">
                  {(rack.columns || []).map((col) => (
                    <div key={col} className="flex-1 text-center text-xs font-semibold text-gray-500">
                      {col}
                    </div>
                  ))}
                </div>

                {/* Rack Grid */}
                <div className="space-y-1">
                  {(rack.rows || []).map((row) => (
                    <div key={row} className="flex gap-1">
                      <div className="w-8 flex items-center justify-center text-xs font-semibold text-gray-500">
                        {row}
                      </div>
                      {(rack.columns || []).map((col) => {
                        const cell = getCellFromRack(rack, col, row);
                        const cellType = CELL_TYPE_COLORS[cell.type] || CELL_TYPE_COLORS.standard;
                        const isBooked = isCellBooked(cell.cell_id);
                        
                        return (
                          <button
                            key={cell.cell_id}
                            onClick={() => handleCellClick(rack, col, row)}
                            className={`flex-1 h-12 rounded cursor-pointer transition-all flex items-center justify-center text-xs font-medium ${
                              isBooked 
                                ? 'bg-red-400 text-white opacity-70' 
                                : `${cellType.bg} ${cellType.text} hover:opacity-80`
                            }`}
                            title={isBooked ? `Booked: ${bookedCells[cell.cell_id]?.party_name}` : `${cellType.label} - Click to book`}
                            data-testid={`cell-${cell.cell_id}`}
                          >
                            {isBooked ? '🔒' : `${col}${row}`}
                          </button>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
        </TabsContent>

        {/* Bookings List Tab Content */}
        <TabsContent value="list" className="mt-4 space-y-4">
          {/* Filters */}
          <Card className="bg-white border-gray-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <Label className="text-gray-700 font-medium">Filter:</Label>
                  <Select value={bookingsFilter} onValueChange={setBookingsFilter}>
                    <SelectTrigger className="w-40 bg-gray-50 border-gray-300">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="all">All Bookings</SelectItem>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="expired">Expired</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={fetchBookingsList}
                  className="border-gray-300"
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${loadingBookings ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Bookings Table */}
          <Card className="bg-white border-gray-200">
            <CardContent className="p-0">
              {loadingBookings ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                </div>
              ) : bookingsList.length === 0 ? (
                <div className="py-12 text-center">
                  <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No bookings found for this store</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="text-gray-700">Booking #</TableHead>
                      <TableHead className="text-gray-700">Type</TableHead>
                      <TableHead className="text-gray-700">Cell/Display</TableHead>
                      <TableHead className="text-gray-700">Party</TableHead>
                      <TableHead className="text-gray-700">Start Date</TableHead>
                      <TableHead className="text-gray-700">End Date</TableHead>
                      <TableHead className="text-gray-700">Status</TableHead>
                      <TableHead className="text-gray-700">Payment</TableHead>
                      <TableHead className="text-gray-700 text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {bookingsList.map((booking) => (
                      <TableRow key={booking.id} className="hover:bg-gray-50">
                        <TableCell className="font-medium text-indigo-600">
                          {booking.booking_number}
                        </TableCell>
                        <TableCell>
                          {booking.cell_id ? (
                            <Badge className="bg-indigo-100 text-indigo-700">
                              <Grid3X3 className="w-3 h-3 mr-1" /> Shelf
                            </Badge>
                          ) : (
                            <Badge className="bg-amber-100 text-amber-700">
                              <Monitor className="w-3 h-3 mr-1" /> Display
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-gray-800">
                          {booking.cell_id || `${booking.rack_id}-${booking.column}`}
                        </TableCell>
                        <TableCell className="text-gray-600">
                          {booking.party_name || '-'}
                        </TableCell>
                        <TableCell className="text-gray-600">
                          {formatIndianDate(booking.start_date)}
                        </TableCell>
                        <TableCell className="font-medium text-amber-700">
                          {formatIndianDate(booking.end_date)}
                        </TableCell>
                        <TableCell>
                          {getExpiryBadge(booking.end_date)}
                        </TableCell>
                        <TableCell>
                          <Badge className={
                            booking.payment_status === 'paid' 
                              ? 'bg-green-100 text-green-700'
                              : booking.payment_status === 'partial'
                              ? 'bg-yellow-100 text-yellow-700'
                              : 'bg-red-100 text-red-700'
                          }>
                            <CreditCard className="w-3 h-3 mr-1" />
                            {booking.payment_status || 'Pending'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleViewBooking(booking)}
                            className="text-indigo-600 hover:text-indigo-800"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Booking Dialog */}
      <Dialog open={bookingDialogOpen} onOpenChange={setBookingDialogOpen}>
        <DialogContent className="bg-white max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader className="pb-2">
            <DialogTitle className="flex items-center gap-2 text-gray-800">
              {bookingType === 'cell' ? (
                <>
                  <Grid3X3 className="w-5 h-5 text-indigo-600" />
                  Book Shelf Cell: {selectedCell?.cell_id}
                </>
              ) : (
                <>
                  <Monitor className="w-5 h-5 text-amber-600" />
                  Book Display Ad: {selectedDisplay?.rack_id}-{selectedDisplay?.column}
                </>
              )}
            </DialogTitle>
            <DialogDescription>
              {bookingType === 'cell' 
                ? `Cell Type: ${CELL_TYPE_COLORS[selectedCell?.type]?.label || 'Standard'}`
                : `Display Type: ${selectedDisplay?.display_type === 'premium' ? 'Premium' : 'Standard'}`
              }
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2">
            {/* Party Type & Selection - Side by Side */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-700 text-sm">Party Type</Label>
                <div className="flex gap-3">
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <input
                      type="radio"
                      name="party_type"
                      value="manufacturer"
                      checked={bookingForm.party_type === 'manufacturer'}
                      onChange={(e) => setBookingForm(p => ({ ...p, party_type: e.target.value, party_id: '' }))}
                      className="text-indigo-600"
                    />
                    <Building className="w-4 h-4 text-indigo-500" />
                    <span className="text-gray-700 text-sm">Manufacturer</span>
                  </label>
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <input
                      type="radio"
                      name="party_type"
                      value="vendor"
                      checked={bookingForm.party_type === 'vendor'}
                      onChange={(e) => setBookingForm(p => ({ ...p, party_type: e.target.value, party_id: '' }))}
                      className="text-indigo-600"
                    />
                    <Truck className="w-4 h-4 text-emerald-500" />
                    <span className="text-gray-700 text-sm">Vendor</span>
                  </label>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-gray-700 text-sm">Select {bookingForm.party_type === 'manufacturer' ? 'Manufacturer' : 'Vendor'} *</Label>
                <Select 
                  value={bookingForm.party_id} 
                  onValueChange={(v) => setBookingForm(p => ({ ...p, party_id: v }))}
                >
                  <SelectTrigger className="bg-gray-50 border-gray-300 h-9">
                    <SelectValue placeholder={`Select ${bookingForm.party_type}`} />
                  </SelectTrigger>
                  <SelectContent className="bg-white z-[9999]">
                    {(bookingForm.party_type === 'manufacturer' ? manufacturers : vendors).map(party => (
                      <SelectItem key={party.id} value={party.id}>
                        {party.company_name || party.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Duration with Discount Info */}
            <div className="space-y-2">
              <Label className="text-gray-700 text-sm">Duration (Select to see discounts)</Label>
              <div className="grid grid-cols-4 gap-2">
                {DURATION_OPTIONS.map(opt => {
                  const discountSlab = bookingSettings?.discount_slabs?.find(s => s.duration_months === opt.value);
                  const discountPercent = discountSlab?.discount_percent || 0;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setBookingForm(p => ({ ...p, duration_months: opt.value }))}
                      className={`py-2 px-2 rounded border text-sm font-medium transition-colors relative ${
                        bookingForm.duration_months === opt.value
                          ? 'bg-indigo-600 text-white border-indigo-600'
                          : 'bg-white text-gray-700 border-gray-300 hover:border-indigo-400'
                      }`}
                    >
                      <div>{opt.label}</div>
                      {discountPercent > 0 && (
                        <div className={`text-xs mt-0.5 ${
                          bookingForm.duration_months === opt.value ? 'text-indigo-200' : 'text-green-600'
                        }`}>
                          {discountPercent}% off
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Start Date & End Date - Side by Side */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-700 text-sm">Start Date</Label>
                <Input
                  type="date"
                  value={bookingForm.start_date}
                  onChange={(e) => setBookingForm(p => ({ ...p, start_date: e.target.value }))}
                  min={new Date().toISOString().split('T')[0]}
                  className="bg-gray-50 border-gray-300 h-9"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-700 text-sm">End Date</Label>
                <div className="h-9 px-3 bg-amber-50 border border-amber-300 rounded-md flex items-center">
                  <Calendar className="w-4 h-4 text-amber-600 mr-2" />
                  <span className="font-medium text-amber-800">
                    {calculateEndDateIndian(bookingForm.start_date, bookingForm.duration_months)}
                  </span>
                </div>
              </div>
            </div>

            {/* Pricing Summary - Compact */}
            {calculatingPrice ? (
              <div className="p-3 bg-gray-50 rounded-lg text-center">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-indigo-600 mx-auto"></div>
                <p className="text-xs text-gray-500 mt-1">Calculating...</p>
              </div>
            ) : pricing ? (
              <div className="p-3 bg-indigo-50 rounded-lg border border-indigo-200">
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                  <div className="flex justify-between text-gray-600">
                    <span>Monthly Rate:</span>
                    <span>₹{pricing.monthly_rate?.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-gray-600">
                    <span>Tax ({pricing.tax_percent}%):</span>
                    <span>₹{pricing.tax_amount_per_month?.toLocaleString()}</span>
                  </div>
                  {pricing.discount_percent > 0 && (
                    <div className="flex justify-between text-green-600 col-span-2">
                      <span>Discount ({pricing.discount_percent}%):</span>
                      <span>-₹{((pricing.monthly_rate - pricing.discounted_monthly_rate) || 0).toLocaleString()}/mo</span>
                    </div>
                  )}
                  <div className="flex justify-between font-semibold text-indigo-800 pt-1 border-t border-indigo-200">
                    <span>Monthly Total:</span>
                    <span>₹{pricing.monthly_amount_with_tax?.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between font-bold text-indigo-900 pt-1 border-t border-indigo-200">
                    <span>Grand Total ({bookingForm.duration_months}mo):</span>
                    <span>₹{pricing.total_amount?.toLocaleString()}</span>
                  </div>
                </div>
              </div>
            ) : null}

            {/* Notes - Compact */}
            <div className="space-y-1">
              <Label className="text-gray-700 text-sm">Notes (Optional)</Label>
              <Textarea
                value={bookingForm.notes}
                onChange={(e) => setBookingForm(p => ({ ...p, notes: e.target.value }))}
                placeholder="Any additional notes..."
                className="bg-gray-50 border-gray-300 text-sm"
                rows={2}
              />
            </div>
          </div>

          <DialogFooter className="pt-2">
            <Button variant="outline" onClick={() => setBookingDialogOpen(false)} className="border-gray-300 h-9">
              Cancel
            </Button>
            <Button 
              onClick={handleConfirmBooking} 
              disabled={!bookingForm.party_id || !pricing}
              className="bg-indigo-600 hover:bg-indigo-700 h-9"
            >
              <Check className="w-4 h-4 mr-2" />
              Book & Generate Invoice
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog */}
      <AlertDialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-amber-500" />
              Confirm Booking
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p>Please confirm the booking details:</p>
                <div className="p-3 bg-gray-50 rounded-lg text-sm space-y-1">
                  <p><strong>Store:</strong> {selectedStore?.name}</p>
                  <p><strong>{bookingType === 'cell' ? 'Cell' : 'Display'}:</strong> {selectedCell?.cell_id || `${selectedDisplay?.rack_id}-${selectedDisplay?.column}`}</p>
                  <p><strong>Party:</strong> {
                    bookingForm.party_type === 'manufacturer'
                      ? manufacturers.find(m => m.id === bookingForm.party_id)?.company_name
                      : vendors.find(v => v.id === bookingForm.party_id)?.name
                  }</p>
                  <p><strong>Duration:</strong> {bookingForm.duration_months} months</p>
                  <p><strong>Period:</strong> {formatIndianDate(bookingForm.start_date)} to {calculateEndDateIndian(bookingForm.start_date, bookingForm.duration_months)}</p>
                  {pricing && (
                    <>
                      <p><strong>Monthly Amount:</strong> ₹{pricing.monthly_amount_with_tax?.toLocaleString()}</p>
                      <p className="text-indigo-700 font-semibold"><strong>Total Amount:</strong> ₹{pricing.total_amount?.toLocaleString()}</p>
                    </>
                  )}
                </div>
                {pricing?.discount_percent > 0 && (
                  <p className="text-amber-700 text-sm">
                    ⚠️ A {pricing.discount_percent}% discount will be applied to all {bookingForm.duration_months} monthly invoices.
                  </p>
                )}
                <p className="text-sm text-gray-600">
                  An invoice for the first month will be generated immediately.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-gray-300">Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleCreateBooking}
              disabled={savingBooking}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              {savingBooking ? 'Creating...' : 'Confirm & Create Booking'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* View Booking Detail Dialog */}
      <Dialog open={viewBookingDialog} onOpenChange={setViewBookingDialog}>
        <DialogContent className="bg-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-gray-800">Booking Details</DialogTitle>
          </DialogHeader>
          {selectedBooking && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Booking Number</p>
                  <p className="font-medium text-gray-800">{selectedBooking.booking_number}</p>
                </div>
                <div>
                  <p className="text-gray-500">Status</p>
                  {getExpiryBadge(selectedBooking.end_date)}
                </div>
                <div>
                  <p className="text-gray-500">Cell/Display</p>
                  <p className="font-medium text-gray-800">{selectedBooking.cell_id || selectedBooking.display_id}</p>
                </div>
                <div>
                  <p className="text-gray-500">Party</p>
                  <p className="font-medium text-gray-800">{selectedBooking.party_name}</p>
                </div>
                <div>
                  <p className="text-gray-500">Start Date</p>
                  <p className="font-medium text-gray-800">{formatIndianDate(selectedBooking.start_date)}</p>
                </div>
                <div>
                  <p className="text-gray-500">End Date</p>
                  <p className="font-medium text-amber-700">{formatIndianDate(selectedBooking.end_date)}</p>
                </div>
                <div>
                  <p className="text-gray-500">Monthly Amount</p>
                  <p className="font-medium text-gray-800">₹{selectedBooking.monthly_amount_with_tax?.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-gray-500">Total Amount</p>
                  <p className="font-medium text-indigo-600">₹{selectedBooking.total_amount?.toLocaleString()}</p>
                </div>
              </div>
              
              {/* Invoice Info */}
              {selectedBooking.first_invoice_id && (
                <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-blue-600" />
                      <span className="text-sm font-medium text-blue-800">Invoice Generated</span>
                    </div>
                    <Badge className={selectedBooking.invoice_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}>
                      {selectedBooking.invoice_status || 'Pending'}
                    </Badge>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ShelfBookingPage;
